import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, ShieldCheck, ShieldOff, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Advisor Helm panel: one-to-one SMS inbox + composer over the existing /api/sms endpoints.
// Conversations are keyed server-side (business number | contact | household); the server enforces
// agent/household authorization, recipient scope, consent (fail-closed) and gating.
export const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};
export const splitKey = (k) => { const [biz, contact, hid] = (k || "").split("|"); return { biz, contact, hid }; };
export const refreshUnread = () => window.dispatchEvent(new Event("sms-unread-refresh"));
export const markThreadRead = async (key) => {
  try { await api.post("/sms/read", { thread_key: key }); refreshUnread(); } catch { /* best effort */ }
};

export const SmsBubble = ({ m }) => (
  <div className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`sms-msg-${m.id}`}>
    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === "outbound" ? "bg-[#0B192C] text-white" : "bg-[#F1F5F9] text-[#0B192C]"}`}>
      <p>{m.body}</p>
      <p className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-white/70" : "text-[#6B7280]"}`}>
        {fmtWhen(m.created_at)} · {m.status}{m.simulated ? " (sim)" : ""}
        {m.status === "send_failed" && <span className="text-[#FCA5A5]"> · failed</span>}
      </p>
    </div>
  </div>
);

export const SmsInbox = ({ initialThread = "", full = false }) => {
  const [me, setMe] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [active, setActive] = useState(initialThread);
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [meRes, inboxRes] = await Promise.all([api.get("/sms/me"), api.get("/sms/inbox")]);
      setMe(meRes.data);
      setMsgs(inboxRes.data.messages || []);
      setLoadErr(false);
    } catch { setLoadErr(true); setMsgs((p) => p || []); setMe((p) => p || {}); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (initialThread) setActive(initialThread); }, [initialThread]);

  // An open conversation is read: mark on open and whenever new inbound arrives while it's open.
  const activeUnread = (msgs || []).filter((m) => m.thread_key === active && m.direction === "inbound" && m.unread).length;
  useEffect(() => { if (active && activeUnread > 0) markThreadRead(active).then(load); }, [active, activeUnread, load]);

  const target = (active ? splitKey(active).contact : to || "").trim();
  useEffect(() => {
    if (!target) { setConsent(null); return; }
    let cancel = false;
    (async () => {
      try { const { data } = await api.get(`/sms/consent`, { params: { phone: target } });
        if (!cancel) setConsent(data); } catch { if (!cancel) setConsent(null); }
    })();
    return () => { cancel = true; };
  }, [target]);

  const send = async () => {
    if (!target) { toast.error("Enter a recipient number."); return; }
    if (!body.trim()) { toast.error("Enter a message."); return; }
    setBusy(true);
    try {
      const hid = active ? splitKey(active).hid : "";
      const { data } = await api.post("/sms/send", { to: target, body, household_id: hid || undefined });
      toast.success(data.simulated ? "Queued (simulated — texting is not live yet)." : "Message sent.");
      setBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send this text.");
    } finally { setBusy(false); }
  };

  const threads = {};
  (msgs || []).forEach((m) => { if (!m.thread_key) return; (threads[m.thread_key] = threads[m.thread_key] || []).push(m); });
  const keys = Object.keys(threads);
  const thread = active ? (threads[active] || []).slice().reverse() : [];
  const noNumber = me && !me.has_number;

  return (
    <div className={`${full ? "" : "mt-6"} rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden`} data-testid="sms-inbox">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F8F8F5] border-b">
        <MessageSquare className="w-4 h-4 text-[#1E3E62]" />
        <h2 className="text-sm font-bold text-[#0B192C]">Text messages</h2>
        {me?.business_number && <span className="text-xs text-[#6B7280]" data-testid="sms-from-number">· your number {me.business_number}</span>}
      </div>
      {msgs === null && <p className="px-4 py-3 text-xs text-[#9CA3AF]" data-testid="sms-loading">Loading your messages…</p>}
      {me && msgs !== null && !me.effective_live && (
        <div className="px-4 py-2 text-[11px] bg-[#FFF7ED] text-[#9A3412] border-b flex items-center gap-1.5" data-testid="sms-sim-banner">
          <AlertTriangle className="w-3 h-3" />
          Texting is not live yet (10DLC campaign {me.campaign_status === "pending_telnyx_review" ? "pending carrier review" : me.campaign_status}). Messages are recorded as a simulation and are not actually delivered.
        </div>
      )}
      {loadErr && <p className="px-4 py-3 text-xs text-[#B91C1C]" data-testid="sms-loaderror">Couldn't load your messages. Please try again shortly.</p>}
      {noNumber && <p className="px-4 py-3 text-xs text-[#9CA3AF]" data-testid="sms-no-number">No SBA messaging number is mapped to your account yet.</p>}

      {msgs !== null && !noNumber && (
        <div className="grid grid-cols-1 sm:grid-cols-3">
          <div className="sm:col-span-1 border-r min-h-[120px] max-h-96 overflow-y-auto">
            {keys.length === 0 && <p className="px-4 py-3 text-xs text-[#9CA3AF]" data-testid="sms-empty">No conversations yet.</p>}
            <ul className="divide-y">
              {keys.map((k) => {
                const { biz, contact } = splitKey(k);
                const rows = threads[k];
                const unread = rows.filter((m) => m.direction === "inbound" && m.unread).length;
                const hname = rows.find((m) => m.household_name)?.household_name;
                return (
                  <li key={k}>
                    <button onClick={() => setActive(k)} data-testid={`sms-thread-${k}`}
                      className={`w-full text-left px-4 py-2.5 hover:bg-[#F8F8F5] ${active === k ? "bg-[#EEF2F6]" : ""}`}>
                      <span className="flex items-center gap-2">
                        <span className={`text-sm ${unread ? "font-bold" : "font-semibold"} text-[#0B192C]`}>{contact}</span>
                        {unread > 0 && <span className="ml-auto text-[10px] font-bold rounded-full bg-[#B91C1C] text-white px-1.5" data-testid={`sms-thread-unread-${k}`}>{unread}</span>}
                      </span>
                      <span className="block text-[11px] text-[#1E3E62]">{hname || "Unmatched — no household"} · via {biz}</span>
                      <span className="block text-[11px] text-[#9CA3AF] truncate">{rows[0]?.body || ""}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="sm:col-span-2 flex flex-col">
            <div className="flex-1 max-h-80 overflow-y-auto px-4 py-3 space-y-2" data-testid="sms-thread">
              {active ? thread.map((m) => <SmsBubble key={m.id} m={m} />)
                : <p className="text-xs text-[#9CA3AF]">Select a conversation or enter a number below to start.</p>}
            </div>
            {target && consent && (
              <div className="px-4 py-1.5 text-[11px] border-t flex items-center gap-1.5" data-testid="sms-consent-state">
                {consent.opted_in
                  ? <><ShieldCheck className="w-3 h-3 text-[#047857]" /> <span className="text-[#047857]">{target} is opted in</span></>
                  : consent.existing_consent
                    ? <><ShieldCheck className="w-3 h-3 text-[#047857]" /> <span className="text-[#047857]" data-testid="sms-existing-consent">Consent on file (SBA lead)</span></>
                  : consent.conversational_reply
                    ? <><ShieldCheck className="w-3 h-3 text-[#1E3E62]" /> <span className="text-[#1E3E62]" data-testid="sms-conversational-reply">{target} texted your line — you can reply to their request (not marketing consent)</span></>
                    : <><ShieldOff className="w-3 h-3 text-[#B91C1C]" /> <span className="text-[#B91C1C]">{target} is not opted in — sending is blocked until a logged opt-in exists</span></>}
              </div>
            )}
            <div className="border-t p-3 flex items-end gap-2">
              <div className="flex-1 space-y-1.5">
                {active ? (
                  <button className="text-[11px] text-[#1E3E62] underline" onClick={() => setActive("")} data-testid="sms-new-conversation">New conversation</button>
                ) : (
                  <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient mobile (+1…)"
                    className="h-9 text-sm" data-testid="sms-to-input" />
                )}
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…"
                  className="h-9 text-sm" data-testid="sms-body-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }} />
              </div>
              <Button onClick={send} disabled={busy || (consent && !consent.opted_in && !consent.conversational_reply && !consent.existing_consent)}
                className="h-9 bg-[#0B192C] text-white shrink-0" data-testid="sms-send-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
